// GE016 V2 — « Pourquoi cette carte ? » (spec §13, explicabilité).
//
// Dialog sombre (DA SmartFeed) alimenté par GET /api/smartfeed/why/<id>/ :
// décomposition du priority_score, faits déterministes (evidences), signal
// primaire et note IA/déterministe. Les cartes legacy V1 renvoient
// `legacy: true` avec les triggers du template — on affiche alors une
// explication simplifiée.
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { smartFeedAPI } from './smartFeedAPI';
import { getTranslation } from './translations';

// Couleurs par zone — alignées sur les en-têtes de sections du dashboard.
export const ZONE_COLORS = {
  NOW: '#ef4444',
  WATCH: '#f59e0b',
  PROGRESS: '#22c55e',
  DISCOVER: '#667eea',
};

// Composantes du score affichées, dans l'ordre de la spec (§6).
const SCORE_COMPONENT_LABELS = {
  severity: { fr: 'Gravité', en: 'Severity' },
  urgency: { fr: 'Urgence', en: 'Urgency' },
  goal_impact: { fr: 'Impact objectifs', en: 'Goal impact' },
  actionability: { fr: 'Actionnabilité', en: 'Actionability' },
  novelty: { fr: 'Nouveauté', en: 'Novelty' },
  personal_relevance: { fr: 'Pertinence perso', en: 'Personal relevance' },
  trend_modulation: { fr: 'Tendance', en: 'Trend' },
  fatigue_penalty: { fr: 'Fatigue', en: 'Fatigue' },
  repetition_penalty: { fr: 'Répétition', en: 'Repetition' },
};

const componentLabel = (key, lang) => {
  const entry = SCORE_COMPONENT_LABELS[key];
  if (!entry) return null; // clés techniques (total, critical_floor_applied…)
  return entry[lang] || entry.fr;
};

const WhyDialog = ({ publicationId, open, onClose, lang = 'fr' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !publicationId) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    setData(null);
    smartFeedAPI
      .getWhy(publicationId)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, publicationId]);

  const explanation = data?.explanation || null;
  const zone = explanation?.zone || 'DISCOVER';
  const zoneColor = ZONE_COLORS[zone] || ZONE_COLORS.DISCOVER;
  const components = explanation?.score_components || {};
  const componentEntries = Object.entries(components)
    .map(([key, value]) => ({ key, label: componentLabel(key, lang), value }))
    .filter((entry) => entry.label && typeof entry.value === 'number' && entry.value !== 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1a1d2e',
          color: '#E0E0E0',
          borderRadius: '16px',
          border: `1px solid ${alpha(zoneColor, 0.3)}`,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
          {getTranslation('whyTitle', lang)}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: alpha('#E0E0E0', 0.7) }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {loading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && !loading && (
          <Alert severity="error" sx={{ my: 1 }}>
            {getTranslation('whyLoadError', lang)}
          </Alert>
        )}

        {explanation && !loading && (
          <Box>
            {/* Zone + score */}
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Chip
                label={getTranslation(`zone${zone.charAt(0)}${zone.slice(1).toLowerCase()}`, lang)}
                size="small"
                sx={{
                  bgcolor: alpha(zoneColor, 0.15),
                  color: zoneColor,
                  fontWeight: 700,
                }}
              />
              {typeof explanation.priority_score === 'number' && (
                <Typography variant="body2" sx={{ color: alpha('#E0E0E0', 0.8) }}>
                  {getTranslation('whyScoreLabel', lang)} : {Math.round(explanation.priority_score)}/100
                </Typography>
              )}
            </Box>

            {/* Raison machine-readable (V2) ou triggers (legacy V1) */}
            {(explanation.reason_code || (explanation.triggers || []).length > 0) && (
              <Box mb={2}>
                <Typography variant="caption" sx={{ color: alpha('#E0E0E0', 0.6), textTransform: 'uppercase', letterSpacing: 1 }}>
                  {getTranslation('whyReasonLabel', lang)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, fontFamily: 'monospace', color: '#E0E0E0' }}>
                  {explanation.reason_code || (explanation.triggers || []).join(' · ')}
                </Typography>
              </Box>
            )}

            {/* Décomposition du score */}
            {componentEntries.length > 0 && (
              <Box mb={2}>
                <Typography variant="caption" sx={{ color: alpha('#E0E0E0', 0.6), textTransform: 'uppercase', letterSpacing: 1 }}>
                  {getTranslation('whyScoreLabel', lang)}
                </Typography>
                {componentEntries.map((entry) => (
                  <Box key={entry.key} display="flex" justifyContent="space-between" mt={0.5}>
                    <Typography variant="body2" sx={{ color: alpha('#E0E0E0', 0.85) }}>
                      {entry.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: entry.value >= 0 ? '#22c55e' : '#ef4444', fontWeight: 600 }}
                    >
                      {entry.value >= 0 ? '+' : ''}{Math.round(entry.value * 10) / 10}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Faits déterministes (evidences) */}
            {(explanation.evidences || []).length > 0 && (
              <Box mb={2}>
                <Typography variant="caption" sx={{ color: alpha('#E0E0E0', 0.6), textTransform: 'uppercase', letterSpacing: 1 }}>
                  {getTranslation('whyFactsLabel', lang)}
                </Typography>
                {explanation.evidences.map((evidence, index) => (
                  <Typography key={index} variant="body2" sx={{ mt: 0.5, color: alpha('#E0E0E0', 0.85) }}>
                    • {evidence.rule || evidence.signal_type}
                  </Typography>
                ))}
              </Box>
            )}

            {/* Note IA / déterministe — transparence (spec §10) */}
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 1, color: alpha('#E0E0E0', 0.5), fontStyle: 'italic' }}
            >
              {getTranslation(explanation.ai_used ? 'whyAiUsed' : 'whyDeterministic', lang)}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WhyDialog;
